/**
 * SearchBar Input Component
 * Extracted to reduce main component complexity
 */

import React from 'react';
import type { SearchBarState } from '../../types/search';
import { searchBarStyles, SearchIcons } from '../SearchBar.styles';

interface SearchBarInputProps {
  inputRef: React.RefObject<HTMLInputElement>;
  inputId: string;
  state: SearchBarState;
  placeholder: string;
  disabled: boolean;
  ariaExpanded: boolean;
  dropdownId: string;
  ariaActivedescendant: string;
  ariaDescribedby: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputFocus: () => void;
  handleInputBlur: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleClearClick: () => void;
  shouldShowClearButton: boolean;
  shouldShowLoadingSpinner: boolean;
  shouldShowSearchIcon: boolean;
}

export const SearchBarInput: React.FC<SearchBarInputProps> = ({
  inputRef,
  inputId,
  state,
  placeholder,
  disabled,
  ariaExpanded,
  dropdownId,
  ariaActivedescendant,
  ariaDescribedby,
  handleInputChange,
  handleInputFocus,
  handleInputBlur,
  handleKeyDown,
  handleClearClick,
  shouldShowClearButton,
  shouldShowLoadingSpinner,
  shouldShowSearchIcon,
}) => {
  return (
    <div className={searchBarStyles.inputWrapper}>
      {/* Search icon on the left side */}
      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 text-slate-400" aria-hidden="true">
        <SearchIcons.Search />
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={state.query}
        placeholder={placeholder}
        disabled={disabled}
        className={`${searchBarStyles.input} ${state.error ? searchBarStyles.inputError : ''} pl-12`}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={ariaExpanded}
        aria-haspopup="listbox"
        aria-owns={dropdownId}
        aria-activedescendant={ariaActivedescendant}
        aria-describedby={ariaDescribedby}
      />

      {/* Clear button */}
      {shouldShowClearButton && (
        <button
          type="button"
          className={searchBarStyles.clearButton}
          onClick={handleClearClick}
          aria-label="Clear search"
          tabIndex={-1}
          style={{ minWidth: '44px', minHeight: '44px' }}
        >
          <SearchIcons.Clear />
        </button>
      )}

      {/* Loading spinner */}
      {shouldShowLoadingSpinner && (
        <div className={searchBarStyles.loadingSpinner} aria-hidden="true">
          <SearchIcons.LoadingSpinner />
        </div>
      )}

      {/* Search icon on the right side (keeping the original) */}
      {shouldShowSearchIcon && (
        <div className={searchBarStyles.searchButton} aria-hidden="true">
          <SearchIcons.Search />
        </div>
      )}
    </div>
  );
};